/**
 * IMS recently viewed products.
 *
 * <ims-recently-viewed-tracker data-product-id="..."> records a product view
 * into localStorage (newest first, deduped, capped).
 *
 * <ims-recently-viewed> reads that history, fetches the matching product
 * cards from the storefront search endpoint via the Section Rendering API,
 * reorders them by recency and reveals the section. With no history (or no
 * matching products) the section stays hidden.
 *
 * The row is a native scroll-snap carousel (see .ims-product-grid--carousel).
 * When the loaded products overflow the viewport, the header prev/next arrows
 * are revealed and page the row one viewport-width at a time.
 */
(() => {
  const STORAGE_KEY = 'ims:recently-viewed';
  const MAX_ENTRIES = 24;

  const readHistory = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((entry) => entry && typeof entry.id === 'number');
    } catch {
      return [];
    }
  };

  const writeHistory = (entries) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
    } catch {
      /* storage unavailable (private mode, quota) — feature degrades silently */
    }
  };

  if (!customElements.get('ims-recently-viewed-tracker')) {
    customElements.define(
      'ims-recently-viewed-tracker',
      class extends HTMLElement {
        connectedCallback() {
          const id = Number(this.dataset.productId);
          if (!id) return;
          const history = readHistory().filter((entry) => entry.id !== id);
          history.unshift({ id, ts: Date.now() });
          writeHistory(history);
        }
      }
    );
  }

  if (!customElements.get('ims-recently-viewed')) {
    customElements.define(
      'ims-recently-viewed',
      class extends HTMLElement {
        connectedCallback() {
          this.load().catch(() => {});
        }

        async load() {
          const limit = Number(this.dataset.limit) || 4;
          const excludeId = Number(this.dataset.exclude) || null;

          // Request a few extra ids so hidden/deleted products don't leave gaps.
          const ids = readHistory()
            .map((entry) => entry.id)
            .filter((id) => id !== excludeId)
            .slice(0, limit + 4);
          if (ids.length === 0) return;

          const params = new URLSearchParams({
            q: ids.map((id) => `id:${id}`).join(' OR '),
            type: 'product',
            'options[unavailable_products]': 'last',
            section_id: this.dataset.sectionId,
          });
          const response = await fetch(`${this.dataset.searchUrl}?${params}`);
          if (!response.ok) return;

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const items = Array.from(doc.querySelectorAll('li[data-product-id]'));
          if (items.length === 0) return;

          // Search results come back in relevance order; restore recency order.
          items.sort(
            (a, b) => ids.indexOf(Number(a.dataset.productId)) - ids.indexOf(Number(b.dataset.productId))
          );

          const grid = this.querySelector('[data-recently-viewed-grid]');
          grid.replaceChildren(...items.slice(0, limit));
          this.hidden = false;
          this.initCarousel(grid);
        }

        initCarousel(track) {
          const nav = this.querySelector('[data-carousel-nav]');
          if (!nav) return;
          const prevButton = nav.querySelector('[data-carousel-prev]');
          const nextButton = nav.querySelector('[data-carousel-next]');

          const update = () => {
            const maxScroll = track.scrollWidth - track.clientWidth;
            nav.hidden = maxScroll <= 1;
            prevButton.toggleAttribute('disabled', track.scrollLeft <= 1);
            nextButton.toggleAttribute('disabled', track.scrollLeft >= maxScroll - 1);
          };

          const page = (direction) => {
            const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            track.scrollBy({
              left: direction * track.clientWidth,
              behavior: reduceMotion ? 'auto' : 'smooth',
            });
          };

          prevButton.addEventListener('click', () => page(-1));
          nextButton.addEventListener('click', () => page(1));
          track.addEventListener('scroll', update, { passive: true });
          // Re-check overflow when the viewport (and therefore item sizing) changes.
          new ResizeObserver(update).observe(track);
          update();
        }
      }
    );
  }
})();
